import 'maplibre-gl/dist/maplibre-gl.css';

import './global.scss';
import './tailwind.css';

import {
  ShareLinkPending,
  ShareLinkUnavailable,
  useShareLinkTarget,
} from 'subApps/ShareLink';
import {enUS, frFR, itIT, jaJP} from '@clerk/localizations';
import {ClerkProvider} from '@clerk/react';
import {withGenericAuthErrors} from 'components/Auth/genericAuthErrors';
import IntercomIdentity from 'components/IntercomIdentity';
import Loader from 'components/Loader';
import {PUBLISHABLE_KEY} from 'constants/index';
import {lazy, memo, Suspense, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';

const EmbeddedAnalytics = lazy(() => import('subApps/EmbeddedAnalytics'));
const AppRoot = lazy(() => import('subApps/AppRoot'));
const ShareFrame = lazy(() => import('subApps/ShareLink/ShareFrame'));
const GoRouter = lazy(() => import('subApps/Go'));
const EmbeddedClerkSignIn = lazy(
  () => import('components/Auth/EmbeddedClerkSignIn'),
);
const EmbeddedClerkSignUp = lazy(
  () => import('components/Auth/EmbeddedClerkSignUp'),
);

function BrandwatchRedirect() {
  const location = useLocation();
  const redirectedPath = location.pathname.replace(
    /^\/brandwatch(?=\/|$)/,
    '/cision-sales',
  );
  return (
    <Navigate
      replace
      to={`${redirectedPath}${location.search}${location.hash}`}
    />
  );
}

function AppProviders() {
  const locales = {
    en: enUS,
    fr: frFR,
    it: itIT,
    ja: jaJP,
  };

  const {i18n, t} = useTranslation();
  const navigate = useNavigate();

  // Clerk re-initialises whenever `localization` changes identity, so only
  // rebuild it when the language actually does.
  const localization = useMemo(
    () =>
      withGenericAuthErrors(
        locales[i18n.language as keyof typeof locales],
        t('auth.genericSignInError'),
      ),
    // biome-ignore lint/correctness/useExhaustiveDependencies: `t` is stable
    // per language; `i18n.language` is what actually invalidates the strings.
    [i18n.language],
  );

  // Above route matching, not as a route: React Router only accepts an overridden
  // location where the matched base is `/`, which is true only at the top level.
  const share = useShareLinkTarget();

  return (
    <Suspense fallback={<Loader />}>
      <ClerkProvider
        // @ts-ignore
        publishableKey={PUBLISHABLE_KEY}
        localization={localization}
        routerPush={(to: string) => navigate(to)}
        routerReplace={(to: string) => navigate(to, {replace: true})}
        signInUrl="/sign-in"
        signUpUrl="/sign-up"
        signInFallbackRedirectUrl="/"
        signUpFallbackRedirectUrl="/"
      >
        <IntercomIdentity />
        {share.status === 'pending' && <ShareLinkPending />}
        {share.status === 'failed' && <ShareLinkUnavailable />}
        {share.status !== 'pending' && share.status !== 'failed' && (
          <>
            <Routes
              location={share.status === 'ready' ? share.targetUrl : undefined}
            >
              {/* Legacy Brandwatch URLs now live under /cision-sales. */}
              <Route element={<BrandwatchRedirect />} path="/brandwatch/*" />
              <Route element={<GoRouter />} path="/go/*" />
              <Route element={<EmbeddedClerkSignIn />} path="/sign-in/*" />
              <Route element={<EmbeddedClerkSignUp />} path="/sign-up/*" />
              <Route
                element={<EmbeddedAnalytics />}
                path="/embedded-analytics"
              />
              {/* A share view renders its own frame: the signed-in chain fetches a
                  user, company, workspace and subscription a grant cannot read. */}
              <Route
                element={
                  share.status === 'ready' ? <ShareFrame /> : <AppRoot />
                }
                path="*"
              />
            </Routes>
          </>
        )}
      </ClerkProvider>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppProviders />
    </BrowserRouter>
  );
}

export default memo(App);
